import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  CalendarBlank,
  CalendarX,
  Check,
  MapPin,
} from 'phosphor-react';
import { WaiverData } from '@/services/waiverService';
import {
  buildBookingPayload,
  isPaymentRequired,
  getWaiverGate,
  deriveSlotLimit,
  entitlementSessionsUsed,
  isCoachLedFacility,
} from '@/utils/bookingRules';
import membershipService, {
  MembershipUsageResponse,
} from '@/services/membership.service';
import WaiverSigningPage from '@/pages/waiver/WaiverSigningPage';
import SBack from '@/components/ui/molecules/SBack';
import SLoader from '@/components/ui/atoms/SLoader';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import SDatePicker from '@/components/ui/molecules/SDatePicker';
import BookingCheckout from '@/components/ui/organisms/BookingCheckout';
import { EntitlementPill } from '@/components/ui/atoms/EntitlementPill';
import BookingPaymentDrawer from '@/components/ui/organisms/BookingPaymentDrawer';
import SFooterDrawer from '@/components/ui/organisms/SFooterDrawer';
import { useBookingAvailability } from '@/hooks/useBookingAvailability';
import {
  useHittingBayAvailability,
  hittingBayAvailabilityKeys,
} from '@/hooks/useHittingBayAvailability';
import { useAvailableDates } from '@/hooks/useAvailableDates';
import { useBookingEligibility } from '@/hooks/useBookingEligibility';
import { getApiMessage } from '@/utils/error';
import { useCreateBooking } from '@/hooks/useCreateBooking';
import { BookingService } from '@/services/booking.service';
import { logger } from '@/utils/logger';
import { useQueryClient } from '@tanstack/react-query';
import { useCalendarSponsorship } from '@/hooks/useCalendarSponsorship';
import { useCalendarSponsorships } from '@/hooks/useCalendarSponsorships';
import {
  useUserLocation,
  getUserLocationFromStorage,
} from '@/hooks/useUserLocation';
import { useUIBookingTypes } from '@/hooks/useBookingTypes';
import { useTodayBookingUsage } from '@/hooks/useBookings';
import {
  FacilityType,
  TeamMember,
  CoachingOptions,
  BookingData,
  Pricing,
  Bay,
  BookingAvailabilityResponse,
  HittingBaySlot,
} from '@/types/booking.types';
import {
  buildSlotUnion,
  buildManualBaySlots,
} from '@/utils/hittingBaySlotUnion';
import { formatTime } from '@/utils/calendar-utils';
import {
  formatDateForAPIWithTimezone,
  formatCalendarDate,
  getDefaultDateString,
} from '@/utils/date-utils';
import { getTodayInTimezone } from '@/utils/timezone-utils';
import type { RootState } from '@/store';
import './SelectSlots.css';
import useSmartLevCheckout from '@/hooks/useSmartLevCheckout';
import {
  InvoicePaymentFrame,
  PaymentFrameSkeleton,
} from '@/v2/components/InvoicePaymentFrame';

// ─── Date strip helpers ────────────────────────────────────────────────────────

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// Number of date chips shown in the strip before the trailing "open full
// calendar" button — must match the `generateDateStrip(calendarReferenceDate, ...)`
// call in the component below.
const DATE_STRIP_VISIBLE_DAYS = 5;

function generateDateStrip(from: Date, count = 7) {
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(from);
    d.setDate(from.getDate() + i);
    return {
      day: DAYS[d.getDay()],
      date: d.getDate(),
      month: MONTHS[d.getMonth()],
      fullDate: d,
    };
  });
}

// "10:00 am" → "10:00 AM" | "2:30 pm" → "2:30 PM"
function abbrevTime(t: string): string {
  return t.replace(/\s?am$/i, ' AM').replace(/\s?pm$/i, ' PM');
}

// Mirrors the backend's bay→calendar-user naming convention
// (api/src/modules/bookings/bookings.service.ts bayNameToLastName /
// BAY_NUMBER_WORDS) so the client can pass the matching team member
// explicitly instead of relying solely on server-side inference.
const BAY_NUMBER_WORDS: Record<number, string> = {
  1: 'one',
  2: 'two',
  3: 'three',
  4: 'four',
  5: 'five',
  6: 'six',
  7: 'seven',
  8: 'eight',
  9: 'nine',
  10: 'ten',
};
function bayNameToLastName(roomName: string): string | null {
  const match = roomName.match(/bay\s+(\d+)/i);
  if (!match) return null;
  return BAY_NUMBER_WORDS[parseInt(match[1], 10)] ?? null;
}

// ─── Component ────────────────────────────────────────────────────────────────

const SelectSlots: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const user = useSelector((state: RootState) => state.auth.user);

  const { t } = useTranslation();
  const { location: userLocation } = useUserLocation();
  const fallbackLocation = getUserLocationFromStorage();

  const selectedLocation =
    location.state?.selectedLocation || userLocation || fallbackLocation;

  // Deep-link support: ?facility=BOX_ASSESSMENT resolves booking type from user's location
  const facilityParam = searchParams.get('facility') as FacilityType | null;
  const isDeepLink = !location.state?.bookingType && !!facilityParam;
  const isReschedule = !!location.state?.rescheduleBookingId;
  // Fetch booking types for deep-link AND reschedule — reschedule nav state omits fields,
  // so we need the API response to get max_mins_per_day and other config fields.
  const { data: deepLinkBookingTypes = [], isLoading: isDeepLinkLoading } =
    useUIBookingTypes((isDeepLink || isReschedule) ? selectedLocation?.id : undefined);
  const resolvedFacility = facilityParam ?? location.state?.bookingType?.facilityType;
  const deepLinkBookingType = deepLinkBookingTypes.find((bt) => bt.facilityType === resolvedFacility);

  const bookingTypeData = isReschedule
    ? { ...location.state?.bookingType, fields: deepLinkBookingType?.fields }
    : location.state?.bookingType ?? deepLinkBookingType;
  const facilityType = (bookingTypeData?.facilityType ??
    facilityParam) as FacilityType;
  const isHittingBay = facilityType === FacilityType.HITTING_BAY;
  const viaEntitlement = !!bookingTypeData?.viaEntitlement;
  const entitlementRemaining: number | undefined = bookingTypeData?.remainingCount;
  const btFields = bookingTypeData?.fields;
  const bookingIncrement = btFields?.booking_increments?.enabled
    ? (btFields.booking_increments.value ?? 30)
    : 30;
  const maxMinsPerDay = btFields?.max_mins_per_day?.enabled
    ? btFields.max_mins_per_day.value ?? null
    : null;
  const queryClient = useQueryClient();
  const rescheduleBookingId: string | undefined =
    location.state?.rescheduleBookingId;
  const rescheduleRoomId: string | undefined = location.state?.rescheduleRoomId;

  // Derive bookingMinDate from selectedLocation.bookingStartDate (if set and valid).
  // Parse only the YYYY-MM-DD portion to avoid UTC-to-local timezone shift.
  const rawBookingMin = selectedLocation?.bookingStartDate
    ? (() => {
        const [y, m, d] = selectedLocation.bookingStartDate
          .slice(0, 10)
          .split('-')
          .map(Number);
        return new Date(y, m - 1, d); // local midnight — no TZ shift
      })()
    : undefined;
  const bookingMinDate =
    rawBookingMin && !isNaN(rawBookingMin.getTime())
      ? rawBookingMin
      : undefined;

  /* A presale member arriving here directly (deep link, browser back, any
     path that skips BookingType's own guard) must not see real availability
     or be able to book — mirrors the API guard in
     BookingsService.assertPresaleBookingWindowOpen (quick task 260918-8hg).
     Blocks when bookingStartDate is unset OR still in the future. Disables
     every availability read below rather than only blocking at render,
     since the reads fire unconditionally. */
  const isPresaleBookingBlocked =
    user?.status === 'presale' &&
    (!selectedLocation?.bookingStartDate ||
      new Date(selectedLocation.bookingStartDate) > new Date());

  const [bookingData, setBookingData] = useState<BookingData>({
    location: {
      name: selectedLocation?.name || 'Tee Box Location',
      address: selectedLocation
        ? [selectedLocation.address?.line1, selectedLocation.address?.city]
            .filter(Boolean)
            .join(', ')
        : '',
    },
    bookingType: bookingTypeData?.title || 'Booking',
    date: location.state?.date || getDefaultDateString(),
    time: location.state?.time || '10:00 am',
    times: location.state?.times || [],
    requestBoxPro: location.state?.requestBoxPro || false,
  });

  const [showWaiverStep, setShowWaiverStep] = useState(false);
  const [waiverForLocation, setWaiverForLocation] = useState<WaiverData | null>(
    null
  );
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useState(false);
  // A declined booking is being handed back. Holds the checkout CTA
  // until the slot is actually free, so a retry cannot race the release
  // and be refused for colliding with its own booking.
  const [isReleasingBooking, setIsReleasingBooking] = useState(false);
  const [isCreatingBookingAfterPayment, setIsCreatingBookingAfterPayment] =
    useState(false);
  const [selectedDateObj, setSelectedDateObj] = useState<Date>(() =>
    getTodayInTimezone(selectedLocation?.timezone)
  );
  const [calendarReferenceDate, setCalendarReferenceDate] = useState<Date>(
    () => {
      const today = getTodayInTimezone(selectedLocation?.timezone);
      return bookingMinDate && bookingMinDate > today ? bookingMinDate : today;
    }
  );
  const [selectedCoachingType, setSelectedCoachingType] = useState<
    string | null
  >(null);
  const [selectedCoach, setSelectedCoach] = useState<TeamMember | null>(null);
  const [selectedBay, setSelectedBay] = useState<string | null>(null);
  const [bayAssignMode, setBayAssignMode] = useState<'auto' | 'manual'>('auto');
  const [isBayDrawerOpen, setIsBayDrawerOpen] = useState(false);
  const [lastFetchedDate, setLastFetchedDate] = useState<string>('');
  const [stableDateForQuery, setStableDateForQuery] = useState<string>('');
  const [membershipUsage, setMembershipUsage] =
    useState<MembershipUsageResponse | null>(null);

  // WR-01: Sync calendarReferenceDate when bookingMinDate resolves asynchronously.
  // The initialiser runs synchronously at mount when selectedLocation may still be undefined,
  // so we keep calendarReferenceDate in sync via a reactive effect.
  React.useEffect(() => {
    if (!bookingMinDate) return;
    const today = getTodayInTimezone(selectedLocation?.timezone);
    if (bookingMinDate > today) {
      setCalendarReferenceDate(new Date(bookingMinDate));
    }
  }, [bookingMinDate?.getTime(), selectedLocation?.timezone]); // eslint-disable-line react-hooks/exhaustive-deps

  // The payment gate, computed once for both the UI (`showPayment`
  // on the checkout drawer) and the confirm handler below. This file
  // previously derived it twice, in two different shapes; both now
  // call the shared rule in @/utils/bookingRules, which the v2 flow
  // uses too.
  const paymentRequired = isPaymentRequired({
    viaEntitlement,
    selectedLocationId: selectedLocation?.id,
    userLocationId: userLocation?.id,
    membershipUsage,
    bookingTypePrice: bookingTypeData?.price,
    bookingTypeFields: btFields,
  });

  // Last known bay/coach roster for this facility's calendar — populated
  // once availability first loads (see the sync effect below) and reused
  // here so we can resolve a hitting-bay's implicit team member *before*
  // issuing this render's availability query, without depending on that
  // same query's own result.
  const roomDetailsRef = useRef<Bay[]>([]);
  const coachesRef = useRef<TeamMember[]>([]);

  // Hitting bay: even when no coach is explicitly selected, a manually
  // picked bay implies a specific team member (same bay-name → last-name
  // convention the backend uses) — pass it through to get_availability too,
  // not just the create-booking payload, so slot availability reflects that
  // team member's calendar.
  const impliedBayTeamMemberId = React.useMemo(() => {
    if (facilityType !== FacilityType.HITTING_BAY || !selectedBay)
      return undefined;
    const bayName = roomDetailsRef.current.find(
      (r: Bay) => r.id === selectedBay
    )?.name;
    const lastName = bayName ? bayNameToLastName(bayName) : null;
    if (!lastName) return undefined;
    return coachesRef.current.find(
      (c: TeamMember) => c.lastName?.toLowerCase() === lastName.toLowerCase()
    )?.id;
  }, [facilityType, selectedBay]);

  const {
    data: availability,
    isLoading,
    error,
    refetch: refetchAvailability,
  } = useBookingAvailability(
    isPresaleBookingBlocked ? '' : selectedLocation?.id || '',
    facilityType,
    stableDateForQuery ||
      formatDateForAPIWithTimezone(
        selectedDateObj,
        selectedLocation?.timezone || 'America/Denver'
      ),
    facilityType === FacilityType.HITTING_BAY
      ? undefined
      : selectedCoach?.id ?? impliedBayTeamMemberId,
    selectedLocation?.timezone,
    rescheduleBookingId,
    /* Never room-scope a coach-led read: GHL intersects roomId with the
       coach's userId and answers empty. `hidesBaySelection` is declared
       below, so the rule is spelled out rather than referenced. */
    facilityType === FacilityType.CLUB_FITTING ||
    facilityType === FacilityType.BOX_ASSESSMENT
      ? undefined
      : selectedBay ?? undefined
  );

  // Hitting bay: aggregated multi-bay availability (active only when isHittingBay)
  // Note: toDateKey is defined later in the component; use inline date-key derivation here
  // to avoid temporal dead-zone reference — same YYYY-MM-DD logic as toDateKey.
  const hittingBayDateKey = isHittingBay
    ? `${selectedDateObj.getFullYear()}-${String(
        selectedDateObj.getMonth() + 1
      ).padStart(2, '0')}-${String(selectedDateObj.getDate()).padStart(2, '0')}`
    : '';

  // Stable start date for the hitting bay query — computed once at mount.
  // The API returns 7 days of availability from this date, so date changes
  // and bay selections should never trigger a re-fetch; we just look up the
  // selected date (hittingBayDateKey) in the already-cached response.
  const [hittingBayFetchDate] = useState<string>(() => {
    if (!isHittingBay) return '';
    const d = getTodayInTimezone(selectedLocation?.timezone);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      '0'
    )}-${String(d.getDate()).padStart(2, '0')}`;
  });

  const { data: hittingBayRaw, isLoading: isHittingBayRawLoading } =
    useHittingBayAvailability(
      isHittingBay && !isPresaleBookingBlocked ? selectedLocation?.id || '' : '',
      hittingBayFetchDate,
      // Discounts the booking being moved, so its own bay and times are not
      // reported busy against it.
      rescheduleBookingId
    );

  // Member gate for hitting bay. Every other facility gets it from
  // get-availability, which refuses with a reason before returning slots, so
  // the reason shows on this screen. The hitting-bay read above is a 7-day,
  // member-agnostic window with no gate — without this a member who cannot
  // book (e.g. a pending membership) only found out on the confirm step.
  const { isLoading: isEligibilityLoading, error: eligibilityError } =
    useBookingEligibility(
      isHittingBay && !isPresaleBookingBlocked ? selectedLocation?.id || '' : '',
      facilityType,
      stableDateForQuery ||
        formatDateForAPIWithTimezone(
          selectedDateObj,
          selectedLocation?.timezone || 'America/Denver'
        ),
      rescheduleBookingId
    );
  // A refusal with a reason empties the day, as a refused get-availability
  // does for every other facility. A transport failure (no message) does not
  // block — add-booking still gates at confirm.
  const hittingBayBlockedMessage = isHittingBay
    ? getApiMessage(eligibilityError)
    : null;
  const hittingBayData = hittingBayBlockedMessage ? undefined : hittingBayRaw;
  const isHittingBayLoading = isHittingBayRawLoading || isEligibilityLoading;

  const createBookingMutation = useCreateBooking();

  // Which of the next 14 days actually have a slot — calendar-wide (not tied to
  // coach/bay selection), fetched once per page visit, purely to pick a sane
  // starting date and let the picker skip/disable dead days.
  const { data: availableDatesMap } = useAvailableDates(
    isPresaleBookingBlocked ? '' : selectedLocation?.id || '',
    facilityType,
    formatDateForAPIWithTimezone(
      calendarReferenceDate,
      selectedLocation?.timezone || 'America/Denver'
    ),
    selectedLocation?.timezone,
    14,
    undefined,
    undefined,
    /* Discounts the booking being moved. Without it this endpoint 400s on
       the once-at-a-time gates and the whole picker reads as dead. */
    rescheduleBookingId
  );

  // Matches the backend's dateKey format (YYYY-MM-DD, local calendar date — see
  // parseMmDdYyyyToYyyyMmDd) so lookups into availableDatesMap line up correctly.
  const toDateKey = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // daily-limit state computed below, after activeHittingBaySlots is available

  // If the currently selected date has zero slots, jump straight to the next
  // date (within the fetched window) that actually has some — no dead clicks.
  React.useEffect(() => {
    if (!availableDatesMap) return;
    const selectedKey = toDateKey(selectedDateObj);
    if (availableDatesMap[selectedKey] !== false) return; // unknown or available — leave as-is
    const nextAvailable = Object.keys(availableDatesMap)
      .filter((key) => availableDatesMap[key] && key > selectedKey)
      .sort()[0];
    if (nextAvailable) {
      const [y, m, d] = nextAvailable.split('-').map(Number);
      const dateObj = new Date(y, m - 1, d);
      // Route through applyDateChange (not setSelectedDateObj directly) so the
      // displayed date string (bookingData.date) stays in sync with the date
      // actually used for the booking payload — otherwise the header keeps
      // showing the old (unavailable) date while silently booking the new one.
      const dateString = `${DAYS[dateObj.getDay()]}, ${
        MONTHS[dateObj.getMonth()]
      } ${dateObj.getDate()}, ${dateObj.getFullYear()}`;
      applyDateChange(dateString, dateObj);

      // Keep the visible strip in sync too: if the earliest available date
      // falls outside the strip's current window, the newly selected date
      // would be highlighted but invisible (strip only ever shows 5 days
      // from calendarReferenceDate). Reposition the window so that date
      // becomes the last visible chip instead of leaving the whole strip
      // showing dead (unavailable) days with the real option hidden off-screen.
      const windowEnd = new Date(calendarReferenceDate);
      windowEnd.setDate(windowEnd.getDate() + (DATE_STRIP_VISIBLE_DAYS - 1));
      if (toDateKey(windowEnd) < nextAvailable) {
        const today = getTodayInTimezone(selectedLocation?.timezone);
        const floor =
          bookingMinDate && bookingMinDate > today ? bookingMinDate : today;
        const newReferenceDate = new Date(dateObj);
        newReferenceDate.setDate(
          newReferenceDate.getDate() - (DATE_STRIP_VISIBLE_DAYS - 1)
        );
        setCalendarReferenceDate(
          newReferenceDate < floor ? floor : newReferenceDate
        );
      }
    }
  }, [availableDatesMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const avail = availability as BookingAvailabilityResponse;
  const coaches = avail?.bookingTypeCalendar?.teamMembers || [];
  const roomDetails = avail?.bookingTypeCalendar?.roomDetails || [];
  const availableSlots = avail?.availableSlots || [];
  const hasCoaches = coaches.length > 0;

  // Sponsorship: resolve calendarId from avail response for sponsor lookups.
  // Hitting bay manual: show sponsor line below selected bay label.
  // Non-hitting-bay: show sponsor block before slot grid.
  // Hitting bay manual: prefetch sponsorship for ALL bays in parallel as soon as
  // hittingBayData loads, so data is cached before the user picks a bay.
  const hittingBayCalendarEntries =
    isHittingBay && bayAssignMode === 'manual' && hittingBayData && selectedLocation?.id
      ? hittingBayData.map((e) => ({ calendarId: e.calendarId, locationId: selectedLocation.id }))
      : [];
  const { byCalendarId: hittingBaySponsorshipMap } = useCalendarSponsorships(
    hittingBayCalendarEntries
  );
  const hittingBaySponsorship = selectedBay
    ? hittingBaySponsorshipMap[selectedBay]
    : undefined;

  // Non-hitting-bay: single lookup from avail response calendarId.
  const regularCalendarId = !isHittingBay
    ? avail?.bookingTypeCalendar?.calendarId || undefined
    : undefined;
  const { data: regularSponsorship } =
    useCalendarSponsorship(regularCalendarId);

  // Club Fitting, Golf Performance Assessment and Coaching Lesson — see
  // isCoachLedFacility in @/utils/bookingRules for why the list is shared.
  const alwaysRequiresCoach = isCoachLedFacility(facilityType);

  // Club fitting / box assessment auto-assign a bay server-side — no picker, no roomId in the payload.
  const hidesBaySelection = alwaysRequiresCoach;

  // Keep the bay/coach roster ref in sync so impliedBayTeamMemberId (computed
  // above, before the availability query) has data to work with on the next
  // render — this calendar config doesn't vary by coach filter, so it's
  // safe to reuse across the "no coach yet" and "implied coach" fetches.
  React.useEffect(() => {
    if (roomDetails.length) roomDetailsRef.current = roomDetails;
    if (coaches.length) coachesRef.current = coaches;
  }, [roomDetails, coaches]);

  // Reschedule: pre-select the bay this booking was already assigned to
  // (passed via rescheduleRoomId from the booking list) so the member isn't
  // forced to re-pick a bay they already have.
  React.useEffect(() => {
    /* `hidesBaySelection` above already says these facilities carry no
       roomId in the payload — the server auto-assigns. Pre-selecting the
       bay anyway put one on the availability call, SmartLev intersected it with
       the coach's userId and answered empty, and the refetch below
       overwrote the good slots with nothing. */
    if (hidesBaySelection) return;
    if (!rescheduleRoomId || selectedBay || !roomDetails.length) return;
    const existingBay = roomDetails.find((b: Bay) => b.id === rescheduleRoomId);
    if (!existingBay) return;
    setSelectedBay(existingBay.id);
    setBookingData((p) => ({ ...p, bayName: existingBay.name }));
    setTimeout(() => refetchAvailability(), 50);
  }, [rescheduleRoomId, roomDetails, selectedBay]); // eslint-disable-line react-hooks/exhaustive-deps

  const noElevatedCoachingTypes = [
    FacilityType.GYM,
    FacilityType.HITTING_BAY,
    FacilityType.PRIVATE_HITTING_BAY,
  ];
  const coachingOptions: CoachingOptions =
    hasCoaches && !noElevatedCoachingTypes.includes(facilityType)
      ? {
          showCoaching: !alwaysRequiresCoach,
          coachingType: 'ELEVATED_COACHING' as any,
          coachingLabel: 'Elevated Coaching',
        }
      : {
          showCoaching: false,
          coachingType: 'STANDARD' as any,
          coachingLabel: '',
        };

  const bookingDataRef = useRef(bookingData);
  React.useEffect(() => {
    bookingDataRef.current = bookingData;
  }, [bookingData]);

  // When slots reload after a bay change, drop the selected times only if they're
  // no longer in the new availability — avoids wiping a still-valid selection.
  React.useEffect(() => {
    if (!bookingData.times.length || !availableSlots.length) return;
    const slotStrings = new Set(
      availableSlots.map((s: any) => `${s.startTime} - ${s.endTime}`)
    );
    const stillValid = bookingData.times.every((t) => slotStrings.has(t));
    if (!stillValid) {
      setBookingData((p) => ({ ...p, time: '', times: [] }));
    }
  }, [availableSlots]);

  // A coach carried over from the previous date is only kept if this day's
  // roster still has them — otherwise the screen would keep filtering
  // availability by someone who isn't working.
  React.useEffect(() => {
    if (!selectedCoach || !coaches.length) return;
    if (coaches.some((c: TeamMember) => c.id === selectedCoach.id)) return;
    setSelectedCoach(null);
    setBookingData((p) => ({ ...p, coachName: undefined }));
  }, [coaches, selectedCoach]);

  React.useEffect(() => {
    if (alwaysRequiresCoach && coaches.length === 1 && !selectedCoach) {
      const c = coaches[0];
      setSelectedCoach(c);
      setBookingData((p) => ({
        ...p,
        coachName: `${c.firstName} ${c.lastName}`,
      }));
    }
  }, [alwaysRequiresCoach, coaches, selectedCoach]);

  React.useEffect(() => {
    const isPrimary = userLocation?.id === selectedLocation?.id;
    if (!isPrimary) {
      membershipService
        .getUsage()
        .then((u) => setMembershipUsage(u))
        .catch(() => setMembershipUsage(null));
    }
  }, [selectedLocation?.id, userLocation?.id]);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleCoachingTypeSelect = (type: string) => {
    if (selectedCoachingType === type) {
      setSelectedCoachingType(null);
      setSelectedCoach(null);
      setBookingData((p) => ({
        ...p,
        coachingType: undefined,
        coachName: undefined,
      }));
    } else {
      setSelectedCoachingType(type);
      setBookingData((p) => ({
        ...p,
        coachingType: type,
        coachName: undefined,
      }));
    }
  };

  const handleCoachSelect = (coach: TeamMember) => {
    if (selectedCoach?.id === coach.id) {
      setSelectedCoach(null);
      setBookingData((p) => ({ ...p, coachName: undefined }));
    } else {
      setSelectedCoach(coach);
      setBookingData((p) => ({
        ...p,
        coachName: `${coach.firstName} ${coach.lastName}`,
      }));
    }
    setSelectedBay(null);
    setBayAssignMode('auto');
    setBookingData((p) => ({ ...p, bayName: undefined, time: '', times: [] }));
  };

  const handleBayModeSelect = (mode: 'auto' | 'manual') => {
    setBayAssignMode(mode);
    if (mode === 'auto') {
      if (selectedBay) {
        setSelectedBay(null);
        setBookingData((p) => ({ ...p, bayName: undefined }));
        if (!isHittingBay) {
          setTimeout(() => refetchAvailability(), 50);
        }
      }
    } else {
      setIsBayDrawerOpen(true);
    }
  };

  const handleBaySelect = (bay: Bay) => {
    if (selectedBay === bay.id) {
      setSelectedBay(null);
      setBookingData((p) => ({ ...p, bayName: undefined }));
    } else {
      setSelectedBay(bay.id);
      setBookingData((p) => ({ ...p, bayName: bay.name }));
    }
    // Hitting bay: all bay slots already present in the single cached response —
    // buildManualBaySlots derives the selected bay's view client-side, no re-fetch needed.
    if (!isHittingBay) {
      setTimeout(() => refetchAvailability(), 50);
    }
  };

  const handleTimeSlotClick = (timeString: string, isSelected: boolean) => {
    if (isSelected) {
      // Any click on an already-selected slot clears all selections
      setBookingData((p) => ({ ...p, time: '', times: [] }));
    } else if (bookingData.times.length === 0) {
      // Nothing selected — select this slot (if daily limit not reached)
      if (!isDailyLimitReached) {
        setBookingData((p) => ({ ...p, time: timeString, times: [timeString] }));
      }
    } else if (bookingData.times.length < maxSelectableSlots) {
      const lastSelected = bookingData.times[bookingData.times.length - 1];
      const selectedEnd = lastSelected.split(' - ')[1];
      const newStart = timeString.split(' - ')[0];
      if (selectedEnd === newStart) {
        // Immediately consecutive — extend the selection
        setBookingData((p) => ({ ...p, times: [...p.times, timeString] }));
      } else {
        // Non-consecutive — replace selection
        setBookingData((p) => ({
          ...p,
          time: timeString,
          times: [timeString],
        }));
      }
    } else {
      // At max slots — start fresh with this slot
      setBookingData((p) => ({ ...p, time: timeString, times: [timeString] }));
    }
  };

  const applyDateChange = (dateString: string, dateObj: Date) => {
    const isSame = dateString === lastFetchedDate;
    const formatted = dateString
      .replace(/^(\w+),/, '$1')
      .replace(/, \d{4}$/, '');
    setBookingData((p) => ({ ...p, date: formatted }));
    setSelectedDateObj(dateObj);
    if (!isSame) {
      const api = formatDateForAPIWithTimezone(
        dateObj,
        selectedLocation?.timezone || 'America/Denver'
      );
      setLastFetchedDate(dateString);
      setStableDateForQuery(api);
      // selectedBay/bayName and selectedCoach intentionally persist across
      // date changes — the bay list (roomDetails) is a static per-facility
      // config, and a coach the member picked is who they want to see on the
      // new date too (club fitting / fitting lesson pick the coach first).
      // Only the time selection resets; the effect below drops the coach if
      // the new day's roster no longer carries them.
      setBookingData((p) => ({
        ...p,
        time: '',
        times: [],
      }));
      // Hitting bay: 7 days of availability already cached; hittingBayDateKey
      // (derived from selectedDateObj) is the lookup key — no re-fetch needed.
      if (!isHittingBay) {
        setTimeout(() => refetchAvailability(), 50);
      }
    }
  };

  const handleStripDateSelect = (d: {
    day: string;
    date: number;
    month: string;
    fullDate: Date;
  }) => {
    const dateString = `${d.day}, ${d.month} ${
      d.date
    }, ${d.fullDate.getFullYear()}`;
    applyDateChange(dateString, d.fullDate);
  };

  const handleDatePickerSelect = (dateString: string, dateObj: Date) => {
    applyDateChange(dateString, dateObj);
    setCalendarReferenceDate(dateObj);
    setIsDatePickerOpen(false);
  };

  // Fee is per session; a run of slots owes the multiple.
  const dropInSlotCount = Math.max(1, bookingData.times.length);


  const pricing: Pricing = {
    fare: (bookingTypeData?.price || 80) * dropInSlotCount,
    technologyFee: 0,
    additionalServices: 0,
    tax: 0,
  };

  const proceedToCheckout = () => {
    const coach = selectedCoach;
    // selectedBay holds a calendarId on the hitting bay path and a room _id
    // otherwise, so the lookup source must match (mirrors selectedBayLabel above).
    const bay = isHittingBay
      ? hittingBayBays.find((b: Bay) => b.id === selectedBay)
      : roomDetails.find((b: any) => b.id === selectedBay);
    const updated = {
      ...bookingData,
      coachingType: selectedCoachingType || undefined,
      coachName: coach ? `${coach.firstName} ${coach.lastName}` : undefined,
      bayName: bay?.name,
      date: bookingData.date || formatCalendarDate(new Date()),
      calendarId: avail?.bookingTypeCalendar?.calendarId || '',
      coachId:
        selectedCoach?.id ||
        coaches.find(
          (c: any) => `${c.firstName} ${c.lastName}` === bookingData.coachName
        )?.id ||
        '',
    };
    setBookingData(updated);
    setIsCheckoutOpen(true);
  };

  const handleNext = async () => {
    // Shared with the v2 flow — see @/utils/bookingRules.
    const waiver = await getWaiverGate(selectedLocation?.id || '');
    if (waiver) {
      setWaiverForLocation(waiver);
      setShowWaiverStep(true);
      return;
    }
    proceedToCheckout();
  };

  // Payload assembly lives in @/utils/bookingRules so the v2 slot
  // picker sends a byte-identical request. Do not inline it back —
  // see the note at the top of that file.
  const buildPayload = (extra?: object) =>
    buildBookingPayload(
      {
        selectedLocation,
        facilityType,
        selectedDateObj,
        bookingData,
        selectedBay,
        hidesBaySelection,
        selectedCoachId: selectedCoach?.id,
        impliedBayTeamMemberId,
        availabilityCalendarId: avail?.bookingTypeCalendar?.calendarId,
        hittingBayManualSlots,
        hittingBayUnionSlots,
        rescheduleBookingId,
      },
      extra
    );

  // Resolve the originally-requested primary bay's display name, but only in
  // auto-assign hitting-bay mode (isHittingBay && !selectedBay). Manual picks
  // must never carry a requestedBayName forward: buildManualBaySlots always
  // returns alternateCalendarIds: [], the server-side retry loop never
  // engages, and a fallback note would misrepresent what happened.
  const resolveRequestedBayName = (payload: { calendarId?: string }) => {
    if (!isHittingBay || selectedBay) return undefined;
    return hittingBayBays.find((b) => b.id === payload.calendarId)?.name;
  };

  const buildSuccessState = (payload: ReturnType<typeof buildPayload>, result: any) => ({
    ...bookingData,
    bookingId: result.id,
    bookingPayload: payload,
    bookingResult: result,
    isRescheduled: !!rescheduleBookingId,
    requestedBayName: resolveRequestedBayName(payload),
    viaEntitlement,
    // ENTITLEMENT_CONSUME is asynchronous via the event framework (D-08), so
    // refetching the balance after confirm would race the decrement and
    // could read a stale pre-decrement count. The pre-booking count minus
    // the number of sessions this booking consumes is the correct value
    // (D-09); Math.max(0, ...) guards a defensive floor against a
    // stale/negative read. The count subtracted is the number of slots
    // this booking spans, which is the same number plan 340-10's
    // computeEntitlementSlotCount derives server-side from the booking's
    // own bounds — so the optimistic display and the eventual ledger
    // write agree without the client being told the answer.
    entitlementRemainingAfter:
      viaEntitlement && typeof entitlementRemaining === 'number'
        ? Math.max(0, entitlementRemaining - entitlementSessionsUsed(bookingData.times.length))
        : undefined,
  });

  // The booking a paid drop-in holds while the card is being taken.
  // Book first, charge second: nothing is charged for a slot that was
  // not actually claimed, and this handle is what the payment drawer's
  // two exits (paid / not paid) act on.
  const pendingBooking = useRef<{
    id: string;
    payload: ReturnType<typeof buildPayload>;
    result: any;
  } | null>(null);


  /* Releases an unpaid booking on any route change while the card form is
     open (back button, tab bar) — exits the drawer's own callbacks never see.
     Unmount-only; `handlePaymentSuccess` clears the ref first, so a paid
     booking is never touched. Killing the app is covered server-side: the
     checkout-session expiry job cancels the booking once its session lapses. */
  useEffect(
    () => () => {
      const pending = pendingBooking.current;
      pendingBooking.current = null;
      if (!pending) return;
      void BookingService.cancelUnpaidBooking(pending.id).catch((err) =>
        logger.error(
          `Failed to release unpaid booking ${pending.id} on leaving the screen:`,
          err?.message ?? err,
        ),
      );
    },
    []
  );

  // Captured at confirm: creating the booking frees its slots from
  // availability and the effect above then clears `bookingData.times`.
  const chargedSlotCountRef = useRef(1);
  /* The same capture as the ref, in state, because it feeds the Stripe
     payment intent through a memo — and `bookingData.times` is empty by
     the time the drawer opens, so deriving it live would tell Stripe
     "1 slot" for a run the member is being charged several slots for. */
  const [chargedSlotCount, setChargedSlotCount] = useState(1);

  /* Payment happens on SmartLev's page — shared with v2 so the two trees
     cannot drift on something this close to money. */
  const checkout = useSmartLevCheckout();

  const paymentBooking = useMemo(
    () =>
      bookingTypeData?.id
        ? { bookingTypeId: bookingTypeData.id, slotCount: chargedSlotCount }
        : undefined,
    [bookingTypeData?.id, chargedSlotCount],
  );

  const handleCheckoutConfirm = async () => {
    chargedSlotCountRef.current = Math.max(1, bookingData.times.length);
    setChargedSlotCount(chargedSlotCountRef.current);
    // Same value the checkout drawer rendered against, so what the
    // member was shown and what happens on confirm cannot disagree.
    // A reschedule edits the existing booking in place server-side — no
    // create call, nothing to roll back — so it keeps the older
    // charge-then-move order.
    if (paymentRequired && rescheduleBookingId) {
      setIsCheckoutOpen(false);
      setIsPaymentDrawerOpen(true);
      return;
    }
    const deferPayment = paymentRequired;
    setBookingError(null);
    try {
      const payload = buildPayload(
        deferPayment ? { deferPayment: true } : undefined
      );
      const result = await createBookingMutation.mutateAsync(payload);

      // Slot is held — the payment drawer takes it from here. The SERVER
      // decides whether anything is owed: no `pending` status means the
      // member owes nothing and must not be shown a card form.
      if (deferPayment && result.paymentStatus === 'pending') {
        pendingBooking.current = { id: result.id, payload, result };
        setIsCheckoutOpen(false);
        setIsPaymentDrawerOpen(true);
        void checkout.start({
          /* The server's figure is what is actually owed; the fallback only
             covers a shape it did not price. */
          amount:
            result.paymentAmount ??
            Math.round((bookingTypeData?.price ?? 0) * 100) * chargedSlotCount,
          currency: 'usd',
          locationId: selectedLocation?.id || '',
          ...(paymentBooking ? { booking: paymentBooking } : {}),
          fulfilment: { bookingId: result.id },
        });
        return;
      }

      navigate('/booking-success', {
        state: buildSuccessState(payload, result),
      });
    } catch (err: any) {
      setBookingError(
        err?.response?.data?.message ||
          err?.message ||
          'Booking failed. Please try again.'
      );
      // Invalidate hitting bay availability so the slot grid refreshes before member retries (T-281-09)
      if (isHittingBay) {
        queryClient.invalidateQueries({
          queryKey: hittingBayAvailabilityKeys.all,
        });
      }
    }
  };

  /* Paid on SmartLev's page. The booking was marked paid by the invoice
     webhook, not here, so there is nothing left to bind. */
  useEffect(() => {
    if (checkout.phase !== 'complete') return;
    const pending = pendingBooking.current;
    pendingBooking.current = null;
    if (!pending) return;

    setIsPaymentDrawerOpen(false);
    navigate('/booking-success', {
      state: buildSuccessState(pending.payload, pending.result),
    });
  }, [checkout.phase]);

  /* Not paid: the slot goes back rather than being held by a booking nobody
     is going to pay for. */
  useEffect(() => {
    if (checkout.phase !== 'failed' || !pendingBooking.current) return;
    void releasePendingBooking(
      checkout.error ?? 'Payment was not completed, so the slot was released.'
    );
  }, [checkout.phase, checkout.error]);

  const handlePaymentSuccess = async (externalPaymentId: string) => {
    const pending = pendingBooking.current;
    // Cleared before the awaits so a drawer dismissal racing this
    // cannot cancel a booking that has just been paid for.
    pendingBooking.current = null;

    setIsCreatingBookingAfterPayment(true);

    // Legacy pay-first path — reschedules only, which have no booking
    // held in advance.
    if (!pending) {
      try {
        const payload = buildPayload({ paymentId: externalPaymentId });
        const result = await createBookingMutation.mutateAsync(payload);
        navigate('/booking-success', {
          state: buildSuccessState(payload, result),
        });
      } catch (err: any) {
        setIsPaymentDrawerOpen(false);
        setIsCheckoutOpen(true);
        setBookingError(
          err?.response?.data?.message ||
            err?.message ||
            'Booking failed after payment. Please contact support.'
        );
      } finally {
        setIsCreatingBookingAfterPayment(false);
      }
      return;
    }

    try {
      await BookingService.confirmBookingPayment(pending.id, externalPaymentId);
    } catch (err: any) {
      // The charge went through and the booking exists — a failed bind
      // is an ops reconciliation, not something to show the member as a
      // booking failure.
      logger.error(
        `Payment ${externalPaymentId} succeeded but could not be attached to booking ${pending.id}:`,
        err?.message ?? err
      );
    }
    setIsCreatingBookingAfterPayment(false);
    navigate('/booking-success', {
      state: buildSuccessState(pending.payload, pending.result),
    });
  };

  // Card declined, or the member dismissed the drawer. Either way the
  // slot goes back — a booking nobody paid for must not hold time.
  const releasePendingBooking = async (message: string) => {
    const pending = pendingBooking.current;
    pendingBooking.current = null;

    setIsCreatingBookingAfterPayment(false);
    setIsPaymentDrawerOpen(false);
    setIsCheckoutOpen(true);
    setBookingError(message);

    if (!pending) return;
    setIsReleasingBooking(true);
    try {
      await BookingService.cancelUnpaidBooking(pending.id);
    } catch (err: any) {
      logger.error(
        `Failed to release unpaid booking ${pending.id}:`,
        err?.message ?? err
      );
    } finally {
      setIsReleasingBooking(false);
    }
  };

  const isNextDisabled = !(
    bookingData.date &&
    bookingData.times?.length > 0 &&
    (!selectedCoachingType || selectedCoach) &&
    (!alwaysRequiresCoach || selectedCoach) &&
    (bayAssignMode !== 'manual' || selectedBay)
  );

  // useMemo hooks must stay above all early returns to satisfy Rules of Hooks.
  const tz = selectedLocation?.timezone || 'America/Denver';

  // Auto-mode slot union (no selectedBay) — priority-ordered merge of all bays
  const hittingBayUnionSlots: HittingBaySlot[] = React.useMemo(() => {
    if (!isHittingBay || !hittingBayData || selectedBay) return [];
    return buildSlotUnion(hittingBayData, hittingBayDateKey, tz);
  }, [isHittingBay, hittingBayData, selectedBay, hittingBayDateKey, tz]);

  // Manual-mode slots — only the selected bay's slots
  const hittingBayManualSlots: HittingBaySlot[] = React.useMemo(() => {
    if (!isHittingBay || !hittingBayData || !selectedBay) return [];
    return buildManualBaySlots(
      hittingBayData,
      hittingBayDateKey,
      tz,
      selectedBay
    );
  }, [isHittingBay, hittingBayData, selectedBay, hittingBayDateKey, tz]);

  // Active hitting bay slot list (union in auto mode, manual bay in manual mode)
  const activeHittingBaySlots: HittingBaySlot[] = isHittingBay
    ? selectedBay
      ? hittingBayManualSlots
      : hittingBayUnionSlots
    : [];

  // ─── Daily limit enforcement ─────────────────────────────────────────────────
  // Hook placed here (after slot arrays) so slotDurationMins can use real data.
  const selectedDateKey = toDateKey(selectedDateObj);

  const { data: todayUsage } = useTodayBookingUsage(
    maxMinsPerDay != null ? (selectedLocation?.id || '') : '',
    maxMinsPerDay != null ? facilityType : '',
    maxMinsPerDay != null ? selectedDateKey : '',
    rescheduleBookingId,
  );
  const todayMinutesBooked = todayUsage?.minutesBooked ?? 0;
  const remainingMins = maxMinsPerDay != null
    ? Math.max(0, maxMinsPerDay - todayMinutesBooked)
    : Infinity;

  // Derive slot duration from actual slot data — safeguards against a
  // misconfigured booking_increments (e.g. 30 when slots are 60 min).
  const parseTimeToMins = (t: string): number => {
    const match = t.trim().match(/^(\d+):(\d+)\s*(AM|PM)$/i);
    if (!match) return 0;
    let h = parseInt(match[1], 10) % 12;
    if (match[3].toUpperCase() === 'PM') h += 12;
    return h * 60 + parseInt(match[2], 10);
  };
  const firstSlot = isHittingBay
    ? activeHittingBaySlots[0]
    : (availableSlots[0] as any);
  const slotDurationMins = (() => {
    if (!firstSlot?.startTime || !firstSlot?.endTime) return bookingIncrement;
    const d = parseTimeToMins(firstSlot.endTime) - parseTimeToMins(firstSlot.startTime);
    return d > 0 ? d : bookingIncrement;
  })();
  // Shared with the v2 flow — see @/utils/bookingRules. The "2" the
  // designs quote is the uncapped fallback, not the rule.
  const { maxSelectableSlots, isDailyLimitReached } = deriveSlotLimit({
    fields: btFields,
    todayMinutesBooked,
    slotDurationMins,
  });

  // ─── Deep-link loading gate ──────────────────────────────────────────────────

  if (isDeepLink && isDeepLinkLoading) {
    return (
      <div className="ss">
        <div className="ss__header">
          <SBack onClick={() => navigate(-1)} />
          <ThemeToggle />
        </div>
        <div className="ss__state">
          <SLoader />
          <span>Loading…</span>
        </div>
      </div>
    );
  }

  // ─── Presale booking-window gate ─────────────────────────────────────────────
  // Reached directly (deep link, browser back) by a presale member at a
  // location with no bookingStartDate, bypassing BookingType's own guard.
  // All availability reads above are already disabled — this just replaces
  // the picker with the same message BookingType shows (quick task 260918-8hg).

  if (isPresaleBookingBlocked) {
    return (
      <div className="ss">
        <div className="ss__header">
          <SBack onClick={() => navigate(-1)} />
          <ThemeToggle />
        </div>
        <div className="ss__state">
          <span>Booking isn&apos;t open at this location yet. We&apos;ll let you know as soon as it is.</span>
        </div>
      </div>
    );
  }

  // ─── Waiver gate ─────────────────────────────────────────────────────────────

  if (showWaiverStep && waiverForLocation) {
    return (
      <WaiverSigningPage
        waiver={waiverForLocation}
        onSigned={() => {
          setShowWaiverStep(false);
          setWaiverForLocation(null);
          proceedToCheckout();
        }}
      />
    );
  }

  // ─── Date strip ──────────────────────────────────────────────────────────────

  const dateStrip = generateDateStrip(
    calendarReferenceDate,
    DATE_STRIP_VISIBLE_DAYS
  );

  const isStripDateSelected = (d: (typeof dateStrip)[0]): boolean => {
    return d.fullDate.toDateString() === selectedDateObj.toDateString();
  };

  const isStripDateDisabled = (d: (typeof dateStrip)[0]): boolean => {
    if (availableDatesMap?.[toDateKey(d.fullDate)] === false) return true;
    if (!bookingMinDate) return false;
    const dateOnly = new Date(
      d.fullDate.getFullYear(),
      d.fullDate.getMonth(),
      d.fullDate.getDate()
    );
    return dateOnly < bookingMinDate;
  };

  // ─── Bay / Room labels ───────────────────────────────────────────────────────
  // roomDetails can arrive from the API in any order — always use each bay's own
  // `name`, never assume array position corresponds to bay number.

  // For hitting bay, derive Bay[] from aggregated data instead of roomDetails.
  // Sort by name numerically so bays always appear as Bay 1, Bay 2, Bay 3
  // regardless of DB priority order (which depends on admin config and may differ).
  const hittingBayBays: Bay[] = isHittingBay
    ? (hittingBayData ?? [])
        .map((entry) => ({ id: entry.calendarId, name: entry.calendarName }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true }))
    : [];

  const sortedRoomDetails: Bay[] = isHittingBay
    ? hittingBayBays
    : [...roomDetails].sort((a: any, b: any) =>
        (a.name || '').localeCompare(b.name || '', undefined, { numeric: true })
      );

  const bayNoun =
    facilityType === FacilityType.WORKSPACE
      ? 'workspace'
      : facilityType === FacilityType.RECOVERY_ROOM
      ? 'recovery room'
      : facilityType === FacilityType.CONFERENCE_ROOM
      ? 'room'
      : 'bay';

  const bayLabelText =
    facilityType === FacilityType.WORKSPACE
      ? 'Select Workspace'
      : facilityType === FacilityType.RECOVERY_ROOM
      ? 'Select Recovery Room'
      : facilityType === FacilityType.CONFERENCE_ROOM
      ? 'Select Room'
      : 'Select Bay';

  // For hitting bay, look up selected bay label from hittingBayBays; otherwise from roomDetails
  const selectedBayLabel = isHittingBay
    ? hittingBayBays.find((b: Bay) => b.id === selectedBay)?.name ?? null
    : roomDetails.find((b: any) => b.id === selectedBay)?.name ?? null;

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="ss">
      {/* Header */}
      <div className="ss__header">
        <SBack onClick={() => navigate(-1)} />
        <ThemeToggle />
      </div>

      {/* Body */}
      <div className="ss__body">
        {/* Booking type + location pill */}
        <div className="ss__meta">
          <h1 className="ss__heading">
            {bookingTypeData?.title || 'Select a Time'}
          </h1>
          <div className="ss__location-pill">
            <MapPin size={12} color="#c99535" weight="fill" />
            <span>{selectedLocation?.name || 'Location'}</span>
          </div>
          {viaEntitlement && <EntitlementPill remainingCount={entitlementRemaining} />}
        </div>

        {/* Golf Performance Assessment description */}
        {facilityType === FacilityType.BOX_ASSESSMENT && (
          <p className="ss__description">
            {t('booking.boxAssessmentDescription')}
          </p>
        )}

        {/* ── Date strip ─────────────────────────────────────────────────────── */}
        <div className="ss__section">
          <p className="ss__section-label">Date</p>
          <div className="ss__date-strip">
            {dateStrip.map((d, i) => {
              const selected = isStripDateSelected(d);
              const stripDisabled = isStripDateDisabled(d);
              return (
                <motion.button
                  key={`${d.day}-${d.date}-${d.month}`}
                  className={`ss__date-chip ${
                    selected ? 'ss__date-chip--selected' : ''
                  } ${stripDisabled ? 'ss__date-chip--disabled' : ''}`}
                  onClick={() => !stripDisabled && handleStripDateSelect(d)}
                  disabled={stripDisabled}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: stripDisabled ? 0.45 : 1, y: 0 }}
                  transition={{
                    duration: 0.18,
                    delay: i * 0.04,
                    ease: 'easeOut',
                  }}
                  whileTap={{ scale: 0.93 }}
                >
                  <span className="ss__date-day">{d.day}</span>
                  <span className="ss__date-num">{d.date}</span>
                  <span className="ss__date-month">{d.month}</span>
                </motion.button>
              );
            })}
            <motion.button
              className="ss__date-more"
              onClick={() => setIsDatePickerOpen(true)}
              whileTap={{ scale: 0.93 }}
            >
              <CalendarBlank size={16} color="#c99535" />
            </motion.button>
          </div>
        </div>

        {/* ── Coaching toggle ─────────────────────────────────────────────────── */}
        {coachingOptions.showCoaching && (
          <div className="ss__section">
            <p className="ss__section-label">Coaching</p>
            <div className="ss__chips">
              <button
                className={`ss__chip ${
                  selectedCoachingType === coachingOptions.coachingType
                    ? 'ss__chip--selected'
                    : ''
                }`}
                onClick={() =>
                  handleCoachingTypeSelect(coachingOptions.coachingType)
                }
              >
                {selectedCoachingType === coachingOptions.coachingType && (
                  <Check size={11} weight="bold" />
                )}
                {coachingOptions.coachingLabel}
              </button>
            </div>
          </div>
        )}

        {/* ── Coach picker ────────────────────────────────────────────────────── */}
        {((coachingOptions.showCoaching &&
          selectedCoachingType &&
          hasCoaches) ||
          (alwaysRequiresCoach && hasCoaches)) && (
          <div className="ss__section">
            <p className="ss__section-label">Coach</p>
            <div className="ss__chips">
              {coaches.map((c: any) => {
                const active = selectedCoach?.id === c.id;
                return (
                  <button
                    key={c.id}
                    className={`ss__chip ${active ? 'ss__chip--selected' : ''}`}
                    onClick={() => handleCoachSelect(c)}
                  >
                    {active && <Check size={11} weight="bold" />}
                    {c.firstName} {c.lastName}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Sponsor block (non-hitting-bay) ────────────────────────────────── */}
        {!isHittingBay &&
          regularSponsorship?.isEnabled &&
          regularSponsorship?.sponsorName && (
            <div className="ss__section ss__section--sponsor">
              <p className="ss__section-label">PRESENTED BY</p>
              <div className="ss__sponsor-block">
                {regularSponsorship.logoUrl && (
                  <img
                    className="ss__sponsor-logo"
                    src={regularSponsorship.logoUrl}
                    alt={regularSponsorship.sponsorName}
                  />
                )}
                <div className="ss__sponsor-info">
                  <span className="ss__sponsor-name">
                    {regularSponsorship.sponsorName}
                  </span>
                  {regularSponsorship.sponsorMessage && (
                    <p className="ss__sponsor-message">
                      {regularSponsorship.sponsorMessage}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

        {/* ── Time slots ──────────────────────────────────────────────────────── */}
        <div className="ss__section">
          <div className="ss__section-header">
            <div>
              <p className="ss__section-label">Select Time</p>
              <p className="ss__slot-hint">Select 1 or 2 consecutive slots</p>
            </div>
            <div className="ss__legend">
              <span className="ss__legend-item ss__legend-item--selected">
                Selected
              </span>
            </div>
          </div>

          {/* Hitting bay loading state */}
          {maxMinsPerDay != null && (
            <div className="ss__daily-limit-banner" style={{
              padding: '8px 12px',
              marginBottom: '8px',
              borderRadius: '8px',
              fontSize: '13px',
              background: isDailyLimitReached ? 'rgba(239,68,68,0.1)' : 'rgba(201,168,76,0.1)',
              color: isDailyLimitReached ? 'var(--text-badge-red)' : 'var(--color-text-gold)',
              textAlign: 'center' as const,
            }}>
              {isDailyLimitReached
                ? `Daily limit reached — you've used all ${maxMinsPerDay} min for today`
                : `${remainingMins} min remaining today (limit: ${maxMinsPerDay} min)`}
            </div>
          )}

          {isHittingBay && isHittingBayLoading && (
            <div className="ss__state">
              <SLoader />
              <span>Loading…</span>
            </div>
          )}

          {/* Hitting bay — member cannot book this date; same treatment as
              the get-availability refusal shown for other facilities */}
          {isHittingBay && !isHittingBayLoading && hittingBayBlockedMessage && (
            <div className="ss__empty-state">
              <div className="ss__empty-icon ss__empty-icon--error">
                <CalendarX size={26} weight="duotone" color="#c0392b" />
              </div>
              <span className="ss__empty-title">No availability</span>
              <span className="ss__empty-sub">{hittingBayBlockedMessage}</span>
            </div>
          )}

          {/* Hitting bay empty state — no bays configured */}
          {isHittingBay &&
            !isHittingBayLoading &&
            !hittingBayBlockedMessage &&
            hittingBayBays.length === 0 && (
              <div className="ss__empty-state">
                <div className="ss__empty-icon">
                  <CalendarBlank size={26} weight="duotone" color="#9e9a90" />
                </div>
                <span className="ss__empty-title">No bays available</span>
                <span className="ss__empty-sub">
                  No bays available. Please contact the location.
                </span>
              </div>
            )}

          {/* Hitting bay slot grid */}
          {isHittingBay &&
            !isHittingBayLoading &&
            hittingBayBays.length > 0 &&
            activeHittingBaySlots.length === 0 && (
              <div className="ss__empty-state">
                <div className="ss__empty-icon">
                  <CalendarBlank size={26} weight="duotone" color="#9e9a90" />
                </div>
                <span className="ss__empty-title">No times available</span>
                <span className="ss__empty-sub">
                  There are no available slots for this date.
                </span>
              </div>
            )}

          {isHittingBay &&
            !isHittingBayLoading &&
            activeHittingBaySlots.length > 0 && (
              <div
                className={
                  hittingBayBays.length > 1 ? 'ss__time-grid-scroll' : undefined
                }
              >
                <div className="ss__time-grid">
                  {activeHittingBaySlots.map(
                    (slot: HittingBaySlot, i: number) => {
                      const timeString = `${slot.startTime} - ${slot.endTime}`;
                      const isSelected = bookingData.times.includes(timeString);
                      const isAdjacent =
                        !isSelected &&
                        bookingData.times.length > 0 &&
                        bookingData.times.length < maxSelectableSlots &&
                        slot.startTime === bookingData.times[bookingData.times.length - 1].split(' - ')[1];
                      const label = abbrevTime(
                        formatTime(slot.startTime, selectedLocation?.timezone)
                      );
                      return (
                        <motion.button
                          key={`${slot.startTime}-${i}`}
                          className={`ss__time-slot ${
                            isSelected ? 'ss__time-slot--selected' : ''
                          } ${isAdjacent ? 'ss__time-slot--adjacent' : ''}`}
                          onClick={() =>
                            handleTimeSlotClick(timeString, isSelected)
                          }
                          initial={{ opacity: 0, scale: 0.92 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{
                            duration: 0.18,
                            delay: Math.min(i * 0.025, 0.28),
                            ease: 'easeOut',
                          }}
                          whileTap={{ scale: 0.94 }}
                        >
                          {label}
                        </motion.button>
                      );
                    }
                  )}
                </div>
              </div>
            )}

          {/* Standard (non-hitting-bay) loading state */}
          {!isHittingBay && isLoading && (
            <div className="ss__state">
              <SLoader />
              <span>Loading slots…</span>
            </div>
          )}

          {!isHittingBay && !isLoading && error && (
            <div className="ss__empty-state">
              <div className="ss__empty-icon ss__empty-icon--error">
                <CalendarX size={26} weight="duotone" color="#c0392b" />
              </div>
              <span className="ss__empty-title">No availability</span>
              <span className="ss__empty-sub">
                {error?.message ||
                  'No slots are available on this date. Try selecting a different day.'}
              </span>
            </div>
          )}

          {!isHittingBay &&
            !isLoading &&
            !error &&
            availableSlots.length === 0 && (
              <div className="ss__empty-state">
                <div className="ss__empty-icon">
                  <CalendarBlank size={26} weight="duotone" color="#9e9a90" />
                </div>
                <span className="ss__empty-title">No slots found</span>
                <span className="ss__empty-sub">
                  There are no available slots for this date.
                </span>
              </div>
            )}

          {!isHittingBay &&
            !isLoading &&
            !error &&
            availableSlots.length > 0 && (
              <div
                className={
                  roomDetails.length > 1 ? 'ss__time-grid-scroll' : undefined
                }
              >
                <div className="ss__time-grid">
                  {availableSlots.map((slot: any, i: number) => {
                    const timeString = `${slot.startTime} - ${slot.endTime}`;
                    const isSelected = bookingData.times.includes(timeString);
                    const disabled = slot.available === false;
                    const isAdjacent =
                      !isSelected &&
                      !disabled &&
                      bookingData.times.length > 0 &&
                      bookingData.times.length < maxSelectableSlots &&
                      !isDailyLimitReached &&
                      slot.startTime === bookingData.times[bookingData.times.length - 1].split(' - ')[1];
                    const label = abbrevTime(
                      formatTime(slot.startTime, selectedLocation?.timezone)
                    );
                    return (
                      <motion.button
                        key={`${slot.startTime}-${i}`}
                        className={`ss__time-slot ${
                          isSelected ? 'ss__time-slot--selected' : ''
                        } ${
                          disabled
                            ? 'ss__time-slot--disabled'
                            : isAdjacent
                            ? 'ss__time-slot--adjacent'
                            : ''
                        }`}
                        disabled={disabled}
                        onClick={() =>
                          handleTimeSlotClick(timeString, isSelected)
                        }
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: disabled ? 0.45 : 1, scale: 1 }}
                        transition={{
                          duration: 0.18,
                          delay: Math.min(i * 0.025, 0.28),
                          ease: 'easeOut',
                        }}
                        whileTap={!disabled ? { scale: 0.94 } : {}}
                      >
                        {label}
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            )}
        </div>

        {/* ── Bay / Room picker ───────────────────────────────────────────────── */}

        {/* Hitting bay: multi-bay mode selector (always shown when hitting bay has ≥1 bay) */}
        {isHittingBay && !isHittingBayLoading && hittingBayBays.length > 0 && (
          <div className="ss__section ss__section--bay">
            <p className="ss__section-label">{bayLabelText}</p>
            <div className="ss__bay-mode-list">
              <button
                type="button"
                className={`ss__bay-mode-row ${
                  bayAssignMode === 'auto' ? 'ss__bay-mode-row--selected' : ''
                }`}
                onClick={() => handleBayModeSelect('auto')}
              >
                <span className="ss__bay-mode-radio">
                  <span className="ss__bay-mode-radio-dot" />
                </span>
                <span className="ss__bay-mode-text">
                  <span className="ss__bay-mode-title ss__bay-mode-title--big">
                    Any available bay
                  </span>
                </span>
              </button>
              <button
                type="button"
                className={`ss__bay-mode-row ${
                  bayAssignMode === 'manual' ? 'ss__bay-mode-row--selected' : ''
                }`}
                onClick={() => handleBayModeSelect('manual')}
              >
                <span className="ss__bay-mode-radio">
                  <span className="ss__bay-mode-radio-dot" />
                </span>
                {selectedBayLabel ? (
                  <span className="ss__bay-mode-text">
                    <span className="ss__bay-mode-title ss__bay-mode-title--big">
                      {selectedBayLabel}
                      {bayAssignMode === 'manual' &&
                        selectedBay &&
                        hittingBaySponsorship?.isEnabled &&
                        hittingBaySponsorship?.sponsorName && (
                          <span className="ss__bay-sponsor">
                            {' | '}
                            {hittingBaySponsorship.sponsorName}
                          </span>
                        )}
                    </span>
                  </span>
                ) : (
                  <span className="ss__bay-mode-text">
                    <span className="ss__bay-mode-title">Choose a bay</span>
                    <span className="ss__bay-mode-sub">{`Choose your preferred ${bayNoun}`}</span>
                  </span>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Standard (non-hitting-bay) single bay card */}
        {!isHittingBay &&
          !hidesBaySelection &&
          availableSlots.length > 0 &&
          roomDetails.length === 1 && (
            <div className="ss__section ss__section--bay">
              <p className="ss__section-label">{bayLabelText}</p>
              <div className="ss__bay-grid">
                <motion.button
                  className={`ss__bay-card ${
                    selectedBay === roomDetails[0].id
                      ? 'ss__bay-card--selected'
                      : ''
                  }`}
                  onClick={() => handleBaySelect(roomDetails[0])}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="ss__bay-name">{roomDetails[0].name}</span>
                </motion.button>
              </div>
            </div>
          )}

        {/* Standard (non-hitting-bay) multi-bay mode selector */}
        {!isHittingBay &&
          !hidesBaySelection &&
          availableSlots.length > 0 &&
          roomDetails.length > 1 && (
            <div className="ss__section ss__section--bay">
              <p className="ss__section-label">{bayLabelText}</p>
              <div className="ss__bay-mode-list">
                <button
                  type="button"
                  className={`ss__bay-mode-row ${
                    bayAssignMode === 'auto' ? 'ss__bay-mode-row--selected' : ''
                  }`}
                  onClick={() => handleBayModeSelect('auto')}
                >
                  <span className="ss__bay-mode-radio">
                    <span className="ss__bay-mode-radio-dot" />
                  </span>
                  <span className="ss__bay-mode-text">
                    <span className="ss__bay-mode-title">Auto Assign</span>
                    <span className="ss__bay-mode-sub">
                      We'll pick the best available {bayNoun} for you
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className={`ss__bay-mode-row ${
                    bayAssignMode === 'manual'
                      ? 'ss__bay-mode-row--selected'
                      : ''
                  }`}
                  onClick={() => handleBayModeSelect('manual')}
                >
                  <span className="ss__bay-mode-radio">
                    <span className="ss__bay-mode-radio-dot" />
                  </span>
                  {selectedBayLabel ? (
                    <span className="ss__bay-mode-text">
                      <span className="ss__bay-mode-title ss__bay-mode-title--big">
                        {selectedBayLabel}
                      </span>
                    </span>
                  ) : (
                    <span className="ss__bay-mode-text">
                      <span className="ss__bay-mode-title">
                        Select Manually
                      </span>
                      <span className="ss__bay-mode-sub">{`Choose your preferred ${bayNoun}`}</span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}
      </div>

      {/* Bay / Room selection drawer */}
      <SFooterDrawer
        isOpen={isBayDrawerOpen}
        onClose={() => {
          setIsBayDrawerOpen(false);
          if (!selectedBay) {
            setBayAssignMode('auto');
          }
        }}
        title={bayLabelText}
        height="50vh"
      >
        <div className="ss__bay-grid">
          {sortedRoomDetails.map((bay: any) => {
            const active = selectedBay === bay.id;
            return (
              <motion.button
                key={bay.id}
                className={`ss__bay-card ${
                  active ? 'ss__bay-card--selected' : ''
                }`}
                onClick={() => {
                  handleBaySelect(bay);
                  setIsBayDrawerOpen(false);
                }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="ss__bay-name">
                  {bay.name}
                  {isHittingBay &&
                    hittingBaySponsorshipMap[bay.id]?.isEnabled &&
                    hittingBaySponsorshipMap[bay.id]?.sponsorName && (
                      <span className="ss__bay-sponsor">
                        {' | '}
                        {hittingBaySponsorshipMap[bay.id]!.sponsorName}
                      </span>
                    )}
                </span>
              </motion.button>
            );
          })}
        </div>
      </SFooterDrawer>

      {/* Footer CTA */}
      <div className="ss__footer">
        <motion.button
          className="ss__cta"
          onClick={handleNext}
          disabled={isNextDisabled}
          animate={{ opacity: isNextDisabled ? 0.45 : 1 }}
          transition={{ duration: 0.2 }}
        >
          Review & confirm
          <ArrowRight size={16} weight="bold" />
        </motion.button>
      </div>

      {/* Date picker full calendar */}
      <SDatePicker
        isOpen={isDatePickerOpen}
        onClose={() => setIsDatePickerOpen(false)}
        selectedDate={
          bookingData.date === 'Select Date' ? undefined : bookingData.date
        }
        onDateSelect={handleDatePickerSelect}
        minDate={bookingMinDate}
        timezone={selectedLocation?.timezone}
        isDateUnavailable={(date) =>
          availableDatesMap?.[toDateKey(date)] === false
        }
      />

      {/* Booking Checkout Drawer */}
      <BookingCheckout
        isOpen={isCheckoutOpen}
        onClose={() => {
          // If closing after an error the slot grid may have refreshed (stale slot removed).
          // Clear the time selection so the stale pick can't silently persist or chain with a new tap.
          if (bookingError)
            setBookingData((p) => ({ ...p, time: '', times: [] }));
          setIsCheckoutOpen(false);
          setBookingError(null);
        }}
        isLoading={createBookingMutation.isPending || isReleasingBooking}
        errorMessage={bookingError ?? undefined}
        showPayment={paymentRequired}
        bookingData={bookingDataRef.current}
        baySponsorName={hittingBaySponsorship?.isEnabled ? hittingBaySponsorship?.sponsorName : undefined}
        pricing={pricing}
        viaEntitlement={viaEntitlement}
        onConfirm={handleCheckoutConfirm}
      />

      {/* SmartLev's payment page, in place — same component v2 uses. A popup
          is not an option inside the native WebView, and confirmation comes
          from the invoice webhook reaching our server rather than from
          anything read back out of the frame. */}
      {isPaymentDrawerOpen && checkout.waiting && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            padding: 16,
            background: 'var(--tb-bg-2, #fff)',
          }}
        >
          {checkout.session?.invoiceUrl ? (
            <InvoicePaymentFrame
              url={checkout.session.invoiceUrl}
              title="Pay for your booking"
            />
          ) : (
            <div style={{ position: 'relative', flex: 1, minHeight: 320 }}>
              <PaymentFrameSkeleton />
            </div>
          )}
        </div>
      )}

      {/* Payment Drawer — reschedules only, which pay before a booking
          exists and so have no invoice to settle. */}
      {isPaymentDrawerOpen && !checkout.waiting && (
        <BookingPaymentDrawer
          isOpen={isPaymentDrawerOpen}
          onClose={() => {
            if (!pendingBooking.current) {
              setIsPaymentDrawerOpen(false);
              return;
            }
            void releasePendingBooking(
              'Payment was not completed, so the slot was released.'
            );
          }}
          locationId={selectedLocation?.id || ''}
          /* Server's figure first — it is what Stripe will debit. The
             fallback is the reschedule path, which creates no booking. */
          price={
            pendingBooking.current?.result?.paymentAmount ??
            (bookingTypeData?.price
              ? Math.round(bookingTypeData.price * 100) *
                chargedSlotCountRef.current
              : 8000 * chargedSlotCountRef.current)
          }
          email={user?.email || ''}
          memberId={user?.crmId || ''}
          /* What the charge is for. Ids and counts only — the API turns
             it into the description Stripe shows. */
          booking={paymentBooking}
          onPaymentSuccess={handlePaymentSuccess}
          /* Only the book-first path owns the failure — it has a booking
             to release. The reschedule path keeps the form's own error
             screen and its retry. */
          onPaymentError={
            rescheduleBookingId
              ? undefined
              : (message) => void releasePendingBooking(message)
          }
          isCreatingBooking={isCreatingBookingAfterPayment}
        />
      )}
    </div>
  );
};

export default SelectSlots;
